/**
 * `LakikaveriInfoRoute` — bespoke route for the `/lakikaveri-info` URL
 * (TA20.2, Phase A PR C.2).
 *
 * Lift posture: prose-into-seed; widgets stay in page (per
 * `phase-a-content-inventory.md` §"Verified inventory"). The article's
 * intro prose + the "Missä asioissa voimme auttaa?" bullet list +
 * closing paragraph lift into `body_html`; the four numbered
 * collapsible steps + the inline ContactModal trigger + per-step PDF
 * lists + per-step internal route links + the closing
 * "Lisätietoja löydät täältä" outbound link stay as page-level chrome.
 *
 * Composition (amended 2026-05-11 per D-PD-003 — the per-article
 * demo-disclosure aside was removed during the proto-parity
 * convergence sweep; demo identity is now carried at the route boundary,
 * see `docs/governance/proto-divergences.md` D-PD-003):
 *
 *   1. H1 carrying `article.title`.
 *   2. Article body via `SanitizedHtml` (FR-049) — the lifted intro
 *      prose + bullet list + closing paragraph.
 *   3. The 4 numbered collapsible steps via the `StepsList`
 *      sub-component (extracted to keep the route file under the
 *      400-line FR-045 ceiling AND to colocate the
 *      `ContactModal`-open `useState` next to the only branch that
 *      consumes it). Each step is a `<details>` with the step's
 *      number + Finnish title in the `<summary>` and the step's
 *      `bodyHtml` + (optionally) ContactModal trigger button + PDF
 *      document links + internal route links inside.
 *   4. Footer outbound link — `<a target="_blank"
 *      rel="noopener noreferrer">` with `metadata.outboundLink`.
 *
 * State matrix mirrors `ArticleDetailPage`: skeleton on pending,
 * re-throw on error, not-found surface on null, populated otherwise.
 *
 * Metadata narrowing
 * ==================
 *
 * `Article.metadata` is `Record<string, unknown>` at the port
 * boundary. The route narrows to `LakikaveriInfoMetadata` (from
 * `@omaplus/shared-types/content-metadata`) via element-by-element
 * type guards. The `steps` array is walked entry-by-entry — each
 * entry must satisfy the `LakikaveriStep` shape, which itself has
 * three optional ReadonlyArray-typed fields (`actions`, `documents`,
 * `internalLinks`) each with their own per-element guards. A shape
 * violation throws so the caller-mounted `PortErrorBoundary`
 * surfaces the diagnostic.
 *
 * Spec references
 * ===============
 *
 *   - `specs/003-content-and-knowledge/tasks.md` TA20.2.
 *   - `specs/003-content-and-knowledge/phase-a-content-inventory.md`
 *     §"Verified inventory" (lift posture), §"Article metadata schema"
 *     (`lakikaveri-info` row).
 *   - `specs/011-pwa-prototype-convergence/spec.md` FR-018 (amended
 *     2026-05-11 per D-PD-003 — demo transparency at route level),
 *     FR-045 (file-size ceiling), FR-049 (no unsanitised HTML).
 */

import type { LakikaveriInfoMetadata } from '@omaplus/shared-types/content-metadata';
import type { JSX } from 'react';
import { AnalyticsRouteReady } from '../../analytics/AnalyticsCaptureProvider.js';
import { useComposeWizard } from '../../member-messaging/compose/wizard/ComposeWizardProvider.js';
import { NotFound } from '../../routes/NotFound.js';
import { useArticle } from '../hooks/useArticle.js';
import { ArticleContentBlocks } from './ArticleContentBlocks.js';
import {
  DetailSurfaceShell,
  DetailSurfaceSkeleton,
  headingClasses,
} from './articleDetailSurface.js';
import { DetailSurfaceHero } from './detailSurfaceHero.js';
import { LakikaveriPopulatedBody } from './LakikaveriPopulatedBody.js';
import { LakikaveriStepsList } from './LakikaveriStepsList.js';
import { isLakikaveriInfoMetadata } from './lakikaveriInfoGuards.js';

const ARTICLE_SLUG = 'lakikaveri-info';

const outboundLinkClasses = 'self-start text-sm font-medium text-primary underline';

export function LakikaveriInfoRoute(): JSX.Element {
  const query = useArticle(ARTICLE_SLUG);
  const composeWizard = useComposeWizard();

  if (query.isPending) {
    return <DetailSurfaceSkeleton testId="lakikaveri-info-route-skeleton" />;
  }

  if (query.isError) {
    throw (
      query.error ?? new Error('useArticle resolved to an error state without an Error instance')
    );
  }

  if (query.data === null) {
    return <NotFound />;
  }

  const article = query.data;
  const hasContentBlocks = article.contentBlocks !== undefined;
  if (hasContentBlocks) {
    return (
      <DetailSurfaceShell as="article">
        <AnalyticsRouteReady pageKey={{ kind: 'memberRoute', route: 'legal-advice-info' }} />
        <DetailSurfaceHero
          imageUrl={article.imageUrl}
          mediaLabel={article.title}
          imageAlt={article.imageAlt}
        />
        <h1 className={headingClasses}>{article.title}</h1>
        <ArticleContentBlocks
          contentBlocks={article.contentBlocks}
          onContactIntent={() => composeWizard.open()}
        />
      </DetailSurfaceShell>
    );
  }

  if (!isLakikaveriInfoMetadata(article.metadata)) {
    throw new Error(
      `LakikaveriInfoRoute: article "${ARTICLE_SLUG}" metadata does not match LakikaveriInfoMetadata shape`,
    );
  }
  const metadata = article.metadata;

  return (
    <DetailSurfaceShell as="article">
      <AnalyticsRouteReady pageKey={{ kind: 'memberRoute', route: 'legal-advice-info' }} />
      <DetailSurfaceHero
        imageUrl={article.imageUrl}
        mediaLabel={article.title}
        imageAlt={article.imageAlt}
      />
      <h1 className={headingClasses}>{article.title}</h1>
      <LakikaveriPopulatedBody bodyHtml={article.bodyHtml} />
      <LakikaveriStepsList steps={metadata.steps} contactLabel={metadata.ctaLabelFi} />
      {metadata.outboundLink === undefined ? null : (
        <a
          className={outboundLinkClasses}
          href={metadata.outboundLink.href}
          target="_blank"
          rel="noopener noreferrer"
        >
          {metadata.outboundLink.labelFi}
        </a>
      )}
    </DetailSurfaceShell>
  );
}

// Re-export the metadata interface for convenience callers; the
// narrowing helper lives in `./lakikaveriInfoGuards.ts` so the route
// file stays focused on composition.
export type { LakikaveriInfoMetadata };
